var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { makeStyles, Slider, useAlert } from "../../../../index";
import { useTranslation } from "../../../../hooks/useTranslation";
import { useMeasure } from "../../../../hooks/useMeasure";
import { getCroppedImageUrl } from "../utils/getCroppedImageUrl";
var ICON_MAX_SIZE = 280;
var MIN_ZOOM = 1;
var MAX_ZOOM = 10;
var SLIDER_HEIGHT = 54;
var SLIDER_STEP = 0.01;
var useStyles = makeStyles(function () { return ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    cropperWrapper: {
        position: 'relative',
        width: 0,
        height: 0,
        maxWidth: ICON_MAX_SIZE,
        maxHeight: ICON_MAX_SIZE,
    },
}); });
export var IconCropper = function (_a) {
    var maxWidth = _a.maxWidth, maxHeight = _a.maxHeight, src = _a.src, imageType = _a.imageType, rotation = _a.rotation, onCropped = _a.onCropped, onError = _a.onError;
    var root = useRef(null);
    var classes = useStyles();
    var alert = useAlert().alert;
    var t = useTranslation().t;
    var _b = useState({ x: 0, y: 0 }), crop = _b[0], setCrop = _b[1];
    var _c = useState(1), zoom = _c[0], setZoom = _c[1];
    var _d = useState(''), displaySrc = _d[0], setDisplaySrc = _d[1];
    var _e = useState(false), isVertical = _e[0], setIsVertical = _e[1];
    var cropperWrapperSize = useMemo(function () {
        if (!maxWidth && !maxHeight) {
            return 0;
        }
        if (!maxWidth || !maxHeight) {
            return maxHeight ? maxHeight - SLIDER_HEIGHT : maxWidth;
        }
        return Math.min(maxWidth, maxHeight - SLIDER_HEIGHT);
    }, [maxHeight, maxWidth]);
    var style = useMeasure({
        width: cropperWrapperSize,
        height: cropperWrapperSize,
    }).style;
    useEffect(function () {
        var image = new Image();
        image.onerror = function () {
            onError === null || onError === void 0 ? void 0 : onError();
        };
        image.onload = function () {
            // 2つの理由からリサイズを行う
            // - 切り取った画像がアイコンの最大サイズを超えないようにするため
            // - iPadでは大きな画像処理が間に合わず空白が切り取られて出力されてしまうため
            // メモリ上限等を防ぐために比率が1:10より大きい画像は使用できないようにする
            if (image.width / image.height > 10 || image.height / image.width > 10) {
                alert(t('縦横比が1：10より大きい画像は使用できません。'));
                onError === null || onError === void 0 ? void 0 : onError();
                return;
            }
            var canvas = document.createElement('canvas');
            var ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }
            var scale = ICON_MAX_SIZE / Math.min(image.width, image.height);
            canvas.width = Math.round(image.width * scale);
            canvas.height = Math.round(image.height * scale);
            ctx.scale(scale, scale);
            // 透過画像の背景色を白にする
            ctx.fillStyle = 'rgba(255, 255, 255, 1)';
            ctx.fillRect(0, 0, image.width, image.height);
            ctx.drawImage(image, 0, 0);
            setIsVertical(image.width >= image.height);
            setDisplaySrc(canvas.toDataURL(imageType));
        };
        image.src = src;
    }, [alert, imageType, onError, src, t]);
    var handleWheel = useCallback(function (event) {
        if (!zoom) {
            return;
        }
        var resultZoom = zoom - Math.round(event.deltaY / 1000);
        if (resultZoom > MAX_ZOOM) {
            setZoom(MAX_ZOOM);
            return;
        }
        if (resultZoom < MIN_ZOOM) {
            setZoom(MIN_ZOOM);
            return;
        }
        // 小数点第二位までになるように四捨五入する
        setZoom(Math.round(resultZoom * 100) / 100);
    }, [zoom]);
    var handleZoom = useCallback(function (value) {
        setZoom(value);
    }, []);
    var handleMediaLoaded = useCallback(function (mediaSize) { return __awaiter(void 0, void 0, void 0, function () {
        var cropSize, dataUrl, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!displaySrc) {
                        // displaySrcが無い時は処理できない
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    cropSize = Math.min(mediaSize.naturalWidth, mediaSize.naturalHeight);
                    return [4 /*yield*/, getCroppedImageUrl(displaySrc, {
                            x: 0,
                            y: 0,
                            width: cropSize,
                            height: cropSize,
                        })];
                case 2:
                    dataUrl = _a.sent();
                    onCropped === null || onCropped === void 0 ? void 0 : onCropped(dataUrl);
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    onError === null || onError === void 0 ? void 0 : onError();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [displaySrc, onCropped, onError]);
    var handleCropComplete = useCallback(function (croppedArea, croppedPixelArea) { return __awaiter(void 0, void 0, void 0, function () {
        var dataUrl, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, getCroppedImageUrl(displaySrc, croppedPixelArea, rotation)];
                case 1:
                    dataUrl = _a.sent();
                    onCropped === null || onCropped === void 0 ? void 0 : onCropped(dataUrl);
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _a.sent();
                    onError === null || onError === void 0 ? void 0 : onError();
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [displaySrc, onCropped, onError, rotation]);
    return (React.createElement("div", { className: classes.root, ref: root },
        React.createElement("div", { className: classes.cropperWrapper, style: style, onWheel: handleWheel },
            React.createElement(Cropper, { image: displaySrc, zoom: zoom, minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM, cropShape: "round", crop: crop, aspect: 1, objectFit: isVertical ? 'vertical-cover' : 'horizontal-cover', rotation: rotation, showGrid: false, onMediaLoaded: handleMediaLoaded, onZoomChange: setZoom, onCropChange: setCrop, onCropComplete: handleCropComplete })),
        React.createElement(Slider, { minWidth: ICON_MAX_SIZE, min: MIN_ZOOM, max: MAX_ZOOM, value: zoom, onChange: handleZoom, sliderStep: SLIDER_STEP })));
};
